/* eslint-disable max-lines */
import React from 'react';
import * as flows from './../flows/async';
import CHUNKS from '../../constants/chunks';
import ChatModule from './../asyncChat';
import Auth from './../Auth';
import { movedPermanently, movedTemporarily, getLanguageBasePath, checkFeature } from 'Helpers/routeHelper';
import { getStore } from 'Helpers/store';
import olxAutosRoutes from '../olxautos/OlxAutosRoutes';
import LazyUniversalComponent from 'Components/LazyUniversalComponent/LazyUniversalComponent';
import {
    onEnterItem,
    onEnterInspection,
    onSetLanguageOnboardingFlag,
    onUnsetLanguageOnboardingFlag,
    onEnterAccount,
    onEnterOrders,
    onEnterSiteMap,
    onEnterListingMain,
    onEnterListing
} from './legacyRouterHooks';
import { ROUTE_CONSTANTS } from 'Constants/posting';
import getIndexRoute from './indexRoute';
import { legacyListingRoutesArray } from 'Constants/legacyRegexConstants';
import getSecurityRoute from './securityRoutes';

const { ATTRIBUTES, VERIFY_ACCOUNT, VERIFY_ACCOUNT_EMAIL, FREE_LIMIT_AD } = ROUTE_CONSTANTS;

const asyncComponentLoader = flows.createAsyncComponentLoader(getStore());

const getListingRoutes = config => {
    const listingRoutes = [
        {
            legacyPath: '(:lang/)items(/q-:text)',
            path: 'items',
            children: [{
                path: ':text'
            }],
            unsafeOnEnter: onEnterListing,
            element: <LazyUniversalComponent
                component={ flows.importListingWrapper() }
                onEnter={ onEnterListing }
                legacyPath={ '(:lang/)items(/q-:text)' }
            />
        }
    ];

    if (!config?.olxAutos?.disableListingRoute) {
        listingRoutes.push(
            {
                legacyPath: legacyListingRoutesArray,
                path: ':listingRoute',
                children: [{
                    path: ':catSlug',
                    children: [{
                        path: ':catSlug2',
                        children: [{
                            path: ':text'
                        }]
                    }]
                }],
                unsafeOnEnter: onEnterListingMain,
                element: <LazyUniversalComponent
                    legacyPath={ legacyListingRoutesArray }
                    onEnter={ onEnterListingMain }
                    component={ flows.importListingWrapper() } />
            }
        );
    }

    return listingRoutes;
};

const getCompareRoute = config => {
    if (!config.olxAutos?.isCarCompareEnabled) {
        return [];
    }
    return [{
        path: CHUNKS.COMPARE,
        unsafeOnEnter: onEnterItem,
        element: <LazyUniversalComponent
            component={ flows.importCompare() }
            onEnter={ onEnterItem }
            isCSR
        />
    }];
};

const getFindOurStoresRoute = config => {
    if (!config.findOurStoresPath || typeof config.findOurStoresPath !== 'string') {
        return [];
    }

    return [{
        legacyPath: `(:lang/)${config.findOurStoresPath}(/:cityName-:geoID)(/:centreName)`,
        path: config.findOurStoresPath,
        children: [{
            path: ':geoID',
            children: [{
                path: ':centreName'
            }]
        }],
        unsafeOnEnter: onUnsetLanguageOnboardingFlag,
        element: <LazyUniversalComponent
            component={ flows.importFindOurStores() }
            onEnter={ onUnsetLanguageOnboardingFlag }
            onLeave={ onSetLanguageOnboardingFlag }
            legacyPath={ `(:lang/)${config.findOurStoresPath}(/:cityName-:geoID)(/:centreName)` }
        />
    }];
};

const contactUsRoute = config => {
    if (!(config.olxAutos && config.olxAutos.isMigratedToPanamera && config.olxAutos.contactUsPath)) {
        return [];
    }
    return [{
        path: config.olxAutos.contactUsPath,
        unsafeOnEnter: onUnsetLanguageOnboardingFlag,
        element: <LazyUniversalComponent
            component={ flows.importContactUs() }
            onEnter={ onUnsetLanguageOnboardingFlag }
            onLeave={ onSetLanguageOnboardingFlag }
        />
    }];
};

const checkIfLoggingRoutesEnabled = (state, replace, config) => {
    if (config.disableLoggingRoutes) {
        replace({
            pathname: `${getLanguageBasePath(state.params)}`,
            state: movedTemporarily()
        });
    }
    else {
        Auth.requiresAuth(state, replace);
    }
};

const checkIfChatEnabled = (state, replace, config) => {
    const isChatEnabled = config?.chat?.newChat?.isChatEnabled || false;

    if (isChatEnabled) {
        Auth.requiresAuth(state, replace);
    }
    else {
        checkIfLoggingRoutesEnabled(state, replace, config);
    }
};

const getSitemapRoutes = config => {
    const sitemapLocale = config?.SEO?.sitemap?.locale || 'sitemap';

    return [{
        legacyPath: `(:lang/)${sitemapLocale}(/:tabName)(/(:geoSlug)_g:geoID)(/(:categorySlug)_c:categoryID)`,
        path: sitemapLocale,
        element: <LazyUniversalComponent
            component={ flows.importSiteMap() }
            onEnter={ onEnterSiteMap }
            legacyPath={ `(:lang/)${sitemapLocale}(/:tabName)(/(:geoSlug)_g:geoID)(/(:categorySlug)_c:categoryID)` }
        />,
        unsafeOnEnter: onEnterSiteMap,
        children: [{
            path: ':tabName',
            children: [{
                path: ':geoID',
                children: [{
                    path: ':categoryID'
                }]
            }]
        }]
    }];
};

const getChildRoutes = config => {
    return [
        getIndexRoute({ config, asyncComponentLoader }),
        ...getListingRoutes(config),
        ...olxAutosRoutes(asyncComponentLoader, config),
        ...getCompareRoute(config),
        ...getSitemapRoutes(config),
        ...getSecurityRoute(config),
        {
            legacyPath: '(:lang/)item/(((:slug)-)iid-):id(/gallery-navigation/:sectionId)',
            path: 'item/:id',
            element: <LazyUniversalComponent
                component={ flows.importItem() }
                legacyPath={ '(:lang/)item/(((:slug)-)iid-):id(/gallery-navigation/:sectionId)' }
                onEnter={ onEnterItem }
                asyncComponentLoader={ asyncComponentLoader }
            />,
            children: [
                {
                    path: 'gallery-navigation/:sectionId'
                }
            ],
            unsafeOnEnter: onEnterItem
        },
        {
            legacyPath: '(:lang/)olxmobbi/item/(((:slug)-)iid-):id(/gallery-navigation/:sectionId)',
            path: '/olxmobbi/item/:id',
            element: <LazyUniversalComponent
                component={ flows.importItem() }
                legacyPath={ '(:lang/)olxmobbi/item/(((:slug)-)iid-):id(/gallery-navigation/:sectionId)' }
                onEnter={ onEnterItem }
                asyncComponentLoader={ asyncComponentLoader }
            />,
            children: [
                {
                    path: 'gallery-navigation/:sectionId'
                }
            ],
            unsafeOnEnter: onEnterItem
        },
        {
            path: 'meeting/pick-date/:id',
            element: <LazyUniversalComponent
                component={ flows.importPickDate() }
            />
        },
        {
            path: '/olxmobbi/meeting/pick-date/:id',
            element: <LazyUniversalComponent
                component={ flows.importPickDate() }
            />
        },
        {
            path: 'meeting/confirmation/:id',
            element: <LazyUniversalComponent
                component={ flows.importConfirmation() }
            />
        },
        {
            path: '/olxmobbi/meeting/confirmation/:id',
            element: <LazyUniversalComponent
                component={ flows.importConfirmation() }
            />
        },
        {
            path: 'nf/chat',
            element: <LazyUniversalComponent
                isCSR
                component={ flows.importChat() }
                onEnter={ checkIfChatEnabled }
            />,
            loader: () => ChatModule.instantiateChat(config),
            unsafeOnEnter: checkIfChatEnabled,
            children: [
                {
                    path: 'new/:itemId/:userId',
                    element: <LazyUniversalComponent
                        component={ flows.importChatThread() }
                        isCSR
                    />,
                    children: [{
                        path: ':amount'
                    }]
                },
                {
                    path: 'user/:userId/:itemId',
                    element: <LazyUniversalComponent
                        component={ flows.importChatThread() }
                        isCSR
                    />
                },
                {
                    path: 'user/:userId',
                    element: <LazyUniversalComponent
                        component={ flows.importChatThread() }
                        isCSR
                    />
                }
            ]
        },
        {
            path: '/olxmobbi/nf/chat',
            element: <LazyUniversalComponent
                isCSR
                component={ flows.importChat() }
                onEnter={ checkIfChatEnabled }
            />,
            loader: () => ChatModule.instantiateChat(config),
            unsafeOnEnter: checkIfChatEnabled,
            children: [
                {
                    path: 'new/:itemId/:userId',
                    element: <LazyUniversalComponent
                        component={ flows.importChatThread() }
                        isCSR
                    />,
                    children: [{
                        path: ':amount'
                    }]
                },
                {
                    path: 'user/:userId/:itemId',
                    element: <LazyUniversalComponent
                        component={ flows.importChatThread() }
                        isCSR
                    />
                },
                {
                    path: 'user/:userId',
                    element: <LazyUniversalComponent
                        component={ flows.importChatThread() }
                        isCSR
                    />
                }
            ]
        },
        {
            path: 'my-zone',
            element: <LazyUniversalComponent
                isCSR
                component={ flows.importMyZone() }
                onEnter={ Auth.requiresAuth }
            />,
            loader: () => ChatModule.instantiateChat(config),
            unsafeOnEnter: Auth.requiresAuth,
            children: [
                {
                    path: 'buy',
                    element: <LazyUniversalComponent
                        component={ flows.importMyZone({ component: 'MyZoneDetailPane' }) }
                        isCSR
                    />
                },
                {
                    path: 'booking',
                    element: <LazyUniversalComponent
                        component={ flows.importMyZone({ component: 'Reservation' }) }
                        isCSR
                    />
                }
            ]
        },
        {
            path: '/olxmobbi/my-zone',
            element: <LazyUniversalComponent
                isCSR
                component={ flows.importMyZone() }
                onEnter={ Auth.requiresAuth }
            />,
            loader: () => ChatModule.instantiateChat(config),
            unsafeOnEnter: Auth.requiresAuth,
            children: [
                {
                    path: 'buy',
                    element: <LazyUniversalComponent
                        component={ flows.importMyZone({ component: 'MyZoneDetailPane' }) }
                        isCSR
                    />
                },
                {
                    path: 'booking',
                    element: <LazyUniversalComponent
                        component={ flows.importMyZone({ component: 'Reservation' }) }
                        isCSR
                    />
                }
            ]
        },
        {
            path: 'nf/items',
            unsafeOnEnter: ({ location }, replace) => {
                replace({
                    pathname: location.pathname.replace(/^\/?nf/, ''),
                    state: movedPermanently()
                });
            },
            children: [{
                path: ':text'
            }]
        },
        {
            path: 'survey',
            element: <LazyUniversalComponent
                component={ flows.importSurvey() }
                isCSR
            />

        },
        {
            path: 'profile',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importProfile() }
                isCSR
            />,
            children: [
                {
                    path: 'reviews/:type'
                }
            ]
        },
        {
            path: 'faq',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importFaq() }
                isCSR
            />
        },
        {
            path: '/olxmobbi/profile',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importProfile() }
                isCSR
            />,
            children: [
                {
                    path: 'reviews/:type'
                }
            ]
        },
        {
            path: 'profile/completion',
            element: <LazyUniversalComponent
                component={ flows.importProfile() }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: '/olxmobbi/profile/completion',
            element: <LazyUniversalComponent
                component={ flows.importProfile() }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'reviews',
            element: <LazyUniversalComponent
                component={ flows.importProfile({ component: 'Reviews' }) }
                onEnter={ Auth.requiresAuth }
                isCSR

            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'notifications',
            element: <LazyUniversalComponent
                component={ flows.importUsercontent({ component: 'NotificationHub' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'myads',
            element: <LazyUniversalComponent
                component={ flows.importUsercontent({ component: 'MyAds' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'lead-to-document/:leadId/:bookingId',
            element: <LazyUniversalComponent
                component={ flows.importUsercontent({ component: 'LeadToDocument' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'myfavorites',
            element: <LazyUniversalComponent
                component={ flows.importUsercontent({ component: 'MyFavorites' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'mybookings',
            element: <LazyUniversalComponent
                component={ flows.importUsercontent({ component: 'MyBookings' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth
        },
        {
            path: 'settings',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importSettings() }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            children: [
                {
                    path: 'password',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ChangePassword' }) }
                        isCSR
                    />
                },
                {
                    path: 'privacy/password',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ChangePassword' }) }
                        isCSR
                    />
                },
                {
                    path: 'privacy/contentControl',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ContentControl' }) }
                        isCSR
                    />
                },
                {
                    path: 'privacy',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'Privacy' }) }
                        isCSR
                    />
                },
                {
                    path: 'notifications',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'Notifications' }) }
                        isCSR
                    />
                },
                {
                    path: 'communicationPreferences',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'Notifications' }) }
                        isCSR
                    />
                },
                {
                    path: 'chat',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ChatNotifications' }) }
                        isCSR
                    />
                },
                {
                    path: 'logoutall',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'LogoutAllDevices' }) }
                        isCSR
                    />
                },
                {
                    path: 'deactivate',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'DeactivateAccount' }) }
                        isCSR
                    />
                }

            ]
        },
        {
            path: 'olxmobbi/settings',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importSettings() }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            children: [
                {
                    path: 'password',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ChangePassword' }) }
                        isCSR
                    />
                },
                {
                    path: 'privacy/password',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ChangePassword' }) }
                        isCSR
                    />
                },
                {
                    path: 'privacy/contentControl',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ContentControl' }) }
                        isCSR
                    />
                },
                {
                    path: 'privacy',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'Privacy' }) }
                        isCSR
                    />
                },
                {
                    path: 'notifications',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'Notifications' }) }
                        isCSR
                    />
                },
                {
                    path: 'communicationPreferences',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'Notifications' }) }
                        isCSR
                    />
                },
                {
                    path: 'chat',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'ChatNotifications' }) }
                        isCSR
                    />
                },
                {
                    path: 'logoutall',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'LogoutAllDevices' }) }
                        isCSR
                    />
                },
                {
                    path: 'deactivate',
                    element: <LazyUniversalComponent
                        component={ flows.importSettings({ component: 'DeactivateAccount' }) }
                        isCSR
                    />
                }

            ]
        },
        {
            path: 'mycredits/billing',
            unsafeOnEnter: ({ params }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/myorders/invoices`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'orders',
            children: [{
                path: ':path'
            }],
            unsafeOnEnter: ({ params }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/myorders/orders/${params.path || ''}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'myorders',
            element: <LazyUniversalComponent
                component={ flows.importMonetization() }
                onEnter={ onEnterOrders }
                isCSR
            />,
            unsafeOnEnter: onEnterOrders,
            children: [
                {
                    path: 'orders',
                    element: <LazyUniversalComponent
                        component={ flows.importMonetization({ component: 'Orders' }) }
                        isCSR
                    />,
                    children: [
                        {
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'Active' }) }
                                isCSR
                            />,
                            index: true
                        },
                        {
                            path: 'active',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'Active' }) }
                                isCSR
                            />
                        },
                        {
                            path: 'scheduled',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'Scheduled' }) }
                                isCSR
                            />
                        },
                        {
                            path: 'expired',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'Expired' }) }
                                isCSR
                            />
                        }]
                },
                {
                    path: 'billing',
                    element: <LazyUniversalComponent
                        component={ flows.importMonetization({ component: 'Billing' }) }
                        isCSR
                    />
                },
                {
                    path: 'invoices',
                    element: <LazyUniversalComponent
                        component={ flows.importMonetization({ component: 'Invoices' }) }
                        isCSR
                    />
                },
                {
                    path: 'booking',
                    element: <LazyUniversalComponent
                        component={ flows.importMonetization({ component: 'Reservation' }) }
                        isCSR
                    />,
                    children: [
                        {
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'ReservationContainer' }) }
                                isCSR
                            />,
                            index: true
                        },
                        {
                            path: 'waitingpayment',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'ReservationContainer' }) }
                                isCSR
                            />
                        },
                        {
                            path: 'waitingconfirmation',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'ReservationContainer' }) }
                                isCSR
                            />
                        },
                        {
                            path: 'onprogress',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'ReservationContainer' }) }
                                isCSR
                            />
                        },
                        {
                            path: 'cancelled',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'ReservationContainer' }) }
                                isCSR
                            />
                        },
                        {
                            path: 'completed',
                            element: <LazyUniversalComponent
                                component={ flows.importMonetization({ component: 'ReservationContainer' }) }
                                isCSR
                            />
                        }]
                }
            ]
        },
        {
            path: 'profile/:userId',
            element: <LazyUniversalComponent
                component={ flows.importProfile({ component: 'PublicProfile' }) }
                isCSR
            />,
            children: [
                {
                    path: 'reviews/:type'
                }
            ]
        },
        {
            path: '/olxmobbi/profile/:userId',
            element: <LazyUniversalComponent
                component={ flows.importProfile({ component: 'PublicProfile' }) }
                isCSR
            />,
            children: [
                {
                    path: 'reviews/:type'
                }
            ]
        },
        {
            path: 'account',
            unsafeOnEnter: onEnterAccount,
            element: <LazyUniversalComponent
                component={ flows.importAccount() }
                onEnter={ onEnterAccount }
                isCSR
            />
        },
        {
            path: 'account/reset',
            unsafeOnEnter: onEnterAccount,
            element: <LazyUniversalComponent
                component={ flows.importAccount({ component: 'ResetPassword' }) }
                unsafeOnEnter={ onEnterAccount }
                isCSR
            />
        },
        {
            path: 'autologin/:token/:id',
            element: <LazyUniversalComponent
                component={ flows.importAccount({ component: 'AutoLogin' }) }
                isCSR
            />
        },
        {
            path: 'autologinV2/:token/:id',
            element: <LazyUniversalComponent
                component={ flows.importAccount({ component: 'AutoLoginV2' }) }
                isCSR
            />
        },
        {
            path: 'dealerlogin/:token',
            unsafeOnEnter: checkFeature(config.superAppAutologin?.enabled),
            element: <LazyUniversalComponent
                component={ flows.importAccount({ component: 'DealerLogin' }) }
                isCSR
            />
        },
        {
            path: 'editProfile',
            element: <LazyUniversalComponent
                component={ flows.importProfile({ component: 'EditProfile' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth,
            children: [
                {
                    index: true,
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'UserData' }) }
                        isCSR
                    />
                },
                {
                    path: 'info',
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'UserData' }) }
                        isCSR
                    />
                },
                {
                    path: 'picture',
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'ProfilePicture' }) }
                        isCSR
                    />
                },
                {
                    path: 'update-premium-info',
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'PremiumProfileUpdate' }) }
                        isCSR
                    />
                }
            ]
        },
        {
            path: 'olxmobbi/editProfile',
            element: <LazyUniversalComponent
                component={ flows.importProfile({ component: 'EditProfile' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            unsafeOnEnter: Auth.requiresAuth,
            children: [
                {
                    index: true,
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'UserData' }) }
                        isCSR
                    />
                },
                {
                    path: 'info',
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'UserData' }) }
                        isCSR
                    />
                },
                {
                    path: 'picture',
                    element: <LazyUniversalComponent
                        component={ flows.importProfile({ component: 'ProfilePicture' }) }
                        isCSR
                    />
                }
            ]
        },
        {
            path: 'post',
            element: <LazyUniversalComponent
                component={ flows.importPosting() }
                onEnter={ checkIfLoggingRoutesEnabled }
                isCSR
            />,
            unsafeOnEnter: checkIfLoggingRoutesEnabled,
            children: [
                {
                    index: true,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'CategoryFirst' }) }
                        isCSR
                    />
                },
                {
                    path: ATTRIBUTES,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'Attributes' }) }
                        isCSR
                    />,
                    children: [
                        {
                            path: ':id'
                        }
                    ],
                    unsafeOnEnter: ({ params }, replace) => {
                        replace({
                            pathname: `${getLanguageBasePath(params)}/post`,
                            state: movedPermanently()
                        });
                    }
                },
                {
                    path: VERIFY_ACCOUNT,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'VerifyAccountPage' }) }
                        isCSR
                    />
                },
                {
                    path: VERIFY_ACCOUNT_EMAIL,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'VerifyAccountEmail' }) }
                        isCSR
                    />
                },
                {
                    path: FREE_LIMIT_AD,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'FreeLimitAd' }) }
                    />,
                    unsafeOnEnter: ({ params }, replace) => {
                        replace({
                            pathname: `${getLanguageBasePath(params)}/post`,
                            state: movedPermanently()
                        });
                    }
                }

            ]
        },
        {
            path: 'edit',
            element: <LazyUniversalComponent
                component={ flows.importPosting() }
                onEnter={ checkIfLoggingRoutesEnabled }
                isCSR
            />,
            unsafeOnEnter: checkIfLoggingRoutesEnabled,
            children: [
                {
                    path: ':id',
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'EditPage' }) }
                        isCSR
                    />
                },
                {
                    path: VERIFY_ACCOUNT,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'VerifyAccountPage' }) }
                        isCSR
                    />
                },
                {
                    path: VERIFY_ACCOUNT_EMAIL,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'VerifyAccountEmail' }) }
                        isCSR
                    />
                },
                {
                    path: FREE_LIMIT_AD,
                    element: <LazyUniversalComponent
                        component={ flows.importPosting({ component: 'FreeLimitAd' }) }
                        isCSR
                    />,
                    unsafeOnEnter: ({ params }, replace) => {
                        replace({
                            pathname: `${getLanguageBasePath(params)}/myads`,
                            state: movedPermanently()
                        });
                    }
                }
            ]
        },
        { // Redirection
            path: 'home',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `/${getLanguageBasePath(params)}${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'share',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/api/share${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'post_item',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/post${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'adding',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/post${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'posting',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/post${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            legacyPath: '(:lang/)ad/-ID(:id)',
            path: 'ad/:id',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/item/iid-${params.id}${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'profile/edit/:id',
            unsafeOnEnter: ({ params }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/editProfile/info`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'olxmobbi/profile/edit/:id',
            unsafeOnEnter: ({ params }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/olxmobbi/editProfile/info`,
                    state: movedPermanently()
                });
            }
        },
        {
            legacy: 'observed(/)(:path)',
            path: 'observed',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/profile${location.search}`,
                    state: movedPermanently()
                });
            },
            children: [{
                path: 'path'
            }]
        },
        {
            path: 'myaccount',
            unsafeOnEnter: ({ params, location }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/profile${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'nf/user/:id',
            unsafeOnEnter: ({ params }, replace) => {
                replace({
                    pathname: `${getLanguageBasePath(params)}/profile/${params.id}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'search',
            unsafeOnEnter: ({ location }, replace) => {
                const { q } = location.query;
                let url = '/items';

                if (q) {
                    url = `${url}/q-${q}`;
                }

                replace({
                    pathname: `${url}${location.search}`,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'm',
            unsafeOnEnter: ({ location, params }, replace) => {
                const { path1, path2, path3, slug } = params;
                const search = location.search;
                const paths = [];

                if (path1) {
                    paths.push(`${path1}/`);
                }
                if (path2) {
                    paths.push(`${path2}/`);
                }
                if (path3) {
                    paths.push(`${path3}/`);
                }
                if (slug) {
                    paths.push(slug);
                }

                replace({
                    pathname: `/${paths.join('')}${search}`,
                    state: movedPermanently()
                });
            },
            children: [{
                path: ':path1',
                children: [
                    {
                        path: ':path2',
                        children: [
                            {
                                path: ':path3',
                                children: [
                                    {
                                        path: ':slug'
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }]
        },
        {

            path: 'payments/cancel',
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'Transition' }) }
                isCSR
            />
        },
        {
            path: 'payments/complete',
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'Transition' }) }
                isCSR
            />
        },
        {
            path: 'payments/packages/consume/:featureType',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment() }
                isCSR
            />,
            children: [
                {
                    path: ':success',
                    children: [{
                        path: ':packageId',
                        children: [{
                            path: ':origin'
                        }]
                    }]
                }
            ]
        },
        {
            path: 'payments/choosePackage/:tag/:featureType',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'ChoosePackage' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            children: [{
                path: ':selectFrom'
            }]
        },
        {
            path: 'payments/viewcart/:featureType',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'BusinessPackagesCart' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />
        },
        {
            path: 'payments/businesspackages',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'BusinessPackages' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            children: [{
                path: ':referrer'
            }]
        },
        {
            path: 'payments/choosePackageMulti/:featureType',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'ChoosePackageMulti' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />,
            children: [{
                path: ':selectFrom'
            }]
        },
        {
            path: 'cart/:location/:category',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'BusinessPackagesCartDeeplink' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />
        },
        {
            path: 'payments/packages/ALL/:location/:category',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPayment({ component: 'FlashSaleDeeplink' }) }
                onEnter={ Auth.requiresAuth }
                isCSR
            />
        },
        {
            path: 'error',
            status: 500,
            element: <LazyUniversalComponent
                component={ flows.importNotFound({ config }) }
            />
        },
        {
            path: 'ads/*',
            unsafeOnEnter: ({ location }, replace) => {
                replace({
                    pathname: location.pathname.replace(/\/ads\//, '/items/'),
                    state: movedPermanently()
                });
            }
        },
        // {
        //     path: 'projects',
        //     unsafeOnEnter: checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)),
        //     element: <LazyUniversalComponent
        //         component={ flows.importReProjects({ component: 'Listing' }) }
        //         onEnter={ checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)) }
        //     />,
        //     children: [
        //         {
        //             path: ':constructionStatus'
        //         }
        //     ]
        // },
        // {
        //     legacyPath: '(:lang/)projects/(:geoSlug)_g(:geoID)',
        //     path: 'projects/:geoID',
        //     unsafeOnEnter: checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)),
        //     element: <LazyUniversalComponent
        //         component={ flows.importReProjects({ component: 'Listing' }) }
        //         onEnter={ checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)) }
        //         legacyPath="(:lang/)projects/(:geoSlug)_g(:geoID)"
        //     />
        // },
        // {
        //     legacyPath: '(:lang/)projects/(:geoSlug)_g(:geoID)/(:constructionStatus)',
        //     path: 'projects/:geoID/:constructionStatus',
        //     unsafeOnEnter: checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)),
        //     element: <LazyUniversalComponent
        //         component={ flows.importReProjects({ component: 'Listing' }) }
        //         onEnter={ checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)) }
        //         legacyPath="(:lang/)projects/(:geoSlug)_g(:geoID)/(:constructionStatus)"
        //     />
        // },
        // {
        //     path: 'project/:projectSlug',
        //     unsafeOnEnter: checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)),
        //     element: <LazyUniversalComponent
        //         component={ flows.importReProjects() }
        //         onEnter={ checkFeature(!(config.olxAutos && config.olxAutos.isMXCL)) }
        //     />,
        //     children: [{
        //         path: 'gallery',
        //         unsafeOnEnter: ({ params }, replace) => {
        //             if (config.olxAutos && config.olxAutos.isMXCL) {
        //                 replace({
        //                     pathname: '/notfound',
        //                     state: movedPermanently()
        //                 });
        //             }
        //             replace({
        //                 pathname: `${getLanguageBasePath(params)}/project/${params.projectSlug}`,
        //                 state: movedPermanently()
        //             });
        //         }
        //     }]
        // },
        {
            path: 'cashmycar/get-started',
            element: <LazyUniversalComponent
                component={ flows.importCashMyCarGetStarted() }
            />
        },
        {
            path: 'cashmycar/information',
            element: <LazyUniversalComponent
                component={ flows.importCashMyCarInformation() }
            />
        },
        {
            path: 'cashmycar/almost-there',
            element: <LazyUniversalComponent
                component={ flows.importCashMyCarAlmostThere() }
            />
        },
        {
            path: 'user-communication',
            element: <LazyUniversalComponent
                component={ flows.importSafetyTips() }
                onEnter={ checkFeature(config.showSafetyTipsPage) }
            />,
            unsafeOnEnter: checkFeature(config.showSafetyTipsPage)
        },
        {
            path: 'filter-categories',
            element: <LazyUniversalComponent
                component={ flows.importCategoryList() }
            />,
            children: [{
                path: ':categoryID'
            }]
        },
        {
            path: 'campaign/olx-stay-home-stay-safe-from-covid-19',
            unsafeOnEnter: checkFeature(config.showCovid19Advisory),
            element: <LazyUniversalComponent
                component={ flows.importCovid19Advisory() }
            />
        },
        {
            path: 'campaign/take-the-time-out-to-declutter',
            unsafeOnEnter: checkFeature(config.showCovid19Advisory),
            element: <LazyUniversalComponent
                component={ flows.importCovid19Declutter() }
            />
        },
        {
            path: 'campaign/covid19-campaign',
            unsafeOnEnter: checkFeature(config.region === 'latam'),
            element: <LazyUniversalComponent
                component={ flows.importCovid19Advisory() }
            />
        },
        {
            path: 'campaign/:campaignURL',
            element: <LazyUniversalComponent
                component={ flows.importCampaigns() }
            />
        },

        {
            path: 'inspection/consent',
            unsafeOnEnter: onEnterInspection,
            element: <LazyUniversalComponent
                component={ flows.importInspectionConsent() }
                onEnter={ onEnterInspection }
                onLeave={ onSetLanguageOnboardingFlag }
                config={ config }
                isCSR
            />
        },
        {
            path: 'book-appointment',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importBookAppointment() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
                config={ config }
            />,
            children: [
                {
                    path: ':screen'
                }
            ]
        },
        {
            path: 'book-inspection',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importBookInspection() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
                config={ config }
            />
        },
        {
            path: 'confirmed-appointment',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importConfirmedAppointment() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
                config={ config }
            />
        },
        {
            path: 'select-map-location',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importSelectMapLocation() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: 'inspection/select-location',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importSelectInspectionLocation() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: '/olxmobbi/inspection/select-location',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importSelectInspectionLocation() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: 'inspection/booking/home',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importInspectionBookingHome() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />,
            children: [
                {
                    path: ':screen'
                }
            ]
        },
        {
            path: 'inspection/booking/store',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importInspectionBookingStore() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />,
            children: [
                {
                    path: ':screen'
                }
            ]
        },
        {
            path: 'inspection/booking/confirmation',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importBookingConfirmation() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: '/olxmobbi/inspection/booking/home',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importInspectionBookingHome() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />,
            children: [
                {
                    path: ':screen'
                }
            ]
        },
        {
            path: '/olxmobbi/inspection/booking/store',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importInspectionBookingStore() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />,
            children: [
                {
                    path: ':screen'
                }
            ]
        },
        {
            path: '/olxmobbi/inspection/booking/confirmation',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importBookingConfirmation() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: 'inspection/booking/noncmc',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importInspectionBookingNonCmcCity() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: '/olxmobbi/inspection/booking/noncmc',
            unsafeOnEnter: onUnsetLanguageOnboardingFlag,
            element: <LazyUniversalComponent
                component={ flows.importInspectionBookingNonCmcCity() }
                onEnter={ onUnsetLanguageOnboardingFlag }
                onLeave={ onSetLanguageOnboardingFlag }
            />
        },
        {
            path: 'membership/:id',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importPremiumUserCheckout() }
                onEnter={ Auth.requiresAuth }
            />
        },
        {
            path: 'self-inspection',
            element: <LazyUniversalComponent
                component={ flows.importSelfInspection() }
            />,
            children: [
                {
                    path: 'error',
                    element: <LazyUniversalComponent
                        component={ flows.importSelfInspection({ component: 'SelfInspectionError' }) }
                    />
                },
                {
                    path: 'success',
                    element: <LazyUniversalComponent
                        component={ flows.importSelfInspection({ component: 'SelfInspectionSuccess' }) }
                    />
                }
            ]
        },
        {
            path: 'assisted-si',
            element: <LazyUniversalComponent
                component={ flows.importAssistedSI() }
            />
        },
        {
            path: 'non-buyable-car',
            element: <LazyUniversalComponent
                component={ flows.importNonBuyableCars() }
            />
        },
        ...contactUsRoute(config),
        ...getFindOurStoresRoute(config),
        // // Handling links from Android and IOS to PWA
        {
            path: 'external_url',
            unsafeOnEnter: ({ location }, replace) => {
                let pathname;
                const searchParam = location.search || '';

                if (searchParam && searchParam.includes('inspection/consent')) {
                    pathname = `/inspection/consent${location.search.split('inspection/consent')[1]}`;
                }

                replace({
                    pathname,
                    state: movedPermanently()
                });
            }
        },
        {
            path: 'covid-support',
            unsafeOnEnter: checkFeature(config.covidSupport),
            element: <LazyUniversalComponent
                component={ flows.importCovidSupport() }
                onEnter={ checkFeature(config.covidSupport) }
            />
        },
        {
            path: 'appointment/lead_tracker',
            element: <LazyUniversalComponent
                component={ flows.importAppRedirection() }
                isCSR
            />
        },
        {
            path: 'terms-conditions',
            element: <LazyUniversalComponent
                component={ flows.importStaticPages({ component: 'TermsAndConditions' }) }
            />
        },
        {
            path: 'policy',
            element: <LazyUniversalComponent
                component={ flows.importStaticPages({ component: 'Policy' }) }
            />
        },
        {
            path: 'contact-details',
            element: <LazyUniversalComponent
                component={ flows.importContactDetails() }
            />
        },
        {
            path: '/olxmobbi/contact-details',
            element: <LazyUniversalComponent
                component={ flows.importContactDetails() }
            />
        },
        {
            path: 'booking',
            children: [
                {
                    path: 'checkout/:id',
                    unsafeOnEnter: Auth.requiresAuth,
                    element: <LazyUniversalComponent
                        component={ flows.importCheckoutReservation() }
                        onEnter={ Auth.requiresAuth }
                    />
                },
                {
                    path: 'detail/:id',
                    unsafeOnEnter: Auth.requiresAuth,
                    element: <LazyUniversalComponent
                        component={ flows.importCheckoutReservation() }
                        onEnter={ Auth.requiresAuth }
                    />
                }]
        },
        {
            path: 'olxmobbi/booking',
            children: [
                {
                    path: 'checkout/:id',
                    unsafeOnEnter: Auth.requiresAuth,
                    element: <LazyUniversalComponent
                        component={ flows.importCheckoutReservation() }
                        onEnter={ Auth.requiresAuth }
                    />
                },
                {
                    path: 'detail/:id',
                    unsafeOnEnter: Auth.requiresAuth,
                    element: <LazyUniversalComponent
                        component={ flows.importCheckoutReservation() }
                        onEnter={ Auth.requiresAuth }
                    />
                }]
        },
        {
            path: '/booking/seller/:id',
            element: <LazyUniversalComponent
                component={ flows.importDetailBooking() }
            />
        },
        {
            path: 'mybenefits',
            element: <LazyUniversalComponent
                component={ flows.importMyBenefit() }
            />
        },
        {
            path: 'mybenefits/voucher',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importVouchers() }
            />
        },
        {
            path: 'mybenefits/voucher/:id',
            element: <LazyUniversalComponent component={ flows.importBenefitDetailVoucher() } />
        },
        {
            path: 'mybenefits/warranty',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importBenefitWarranty() }
            />
        },
        {
            path: 'mybenefits/warranty/:id',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent component={ flows.importBenefitDetailWarranty() } />
        },
        {
            path: 'mybenefits/voucher/:id',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent component={ flows.importBenefitDetailVoucher() } />
        },
        {
            path: 'mybenefits/insurance',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importBenefitInsurance() }
            />
        },
        {
            path: 'mybenefits/insurance/:id',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent component={ flows.importBenefitDetailInsurance() } />
        },
        {
            path: 'mybenefits/insurance-form-submission',
            unsafeOnEnter: Auth.requiresAuth,
            element: <LazyUniversalComponent
                component={ flows.importBenefitFormSubmission() }
            />
        },
        {
            path: 'notfound',
            status: 404,
            element: <LazyUniversalComponent
                component={ flows.importNotFound({ config }) }
            />
        },
        {
            path: '*',
            status: 404,
            element: <LazyUniversalComponent
                component={ flows.importNotFound({ config }) }
            />
        },
        /* NOT FOR MERGE ONLY FOR TESTING */
        {
            path: 'olxmobbi',
            element: <LazyUniversalComponent
                component={ flows.importOlxautos() }
            />
        }

    ];
};

export default getChildRoutes;
